import {
  APPLIED_FILTER_FIELDS_FRAGMENT,
  BLOCK_FRAGMENT,
  COLOR_FIELDS_FRAGMENT,
  CONTROL_FIELDS_FRAGMENT,
  CURRENCY_FRAGMENT,
  DELIVEROO_ICON_FRAGMENT,
  FULFILLMENT_METHOD_FIELDS_FRAGMENT,
  FULFILLMENT_TIME_OPTION_FIELDS_FRAGMENT,
  LAYOUT_FIELDS_FRAGMENT,
  MAP_BLOCK_FRAGMENT,
  MAP_LAYOUT_FIELDS_FRAGMENT,
  QUERY_RESULTS_FRAGMENT,
  RESTAURANT_FRAGMENT,
  SEARCH_BLOCK_FRAGMENT,
  SEARCH_FIELDS_FRAGMENT,
  SEARCH_LAYOUT_FIELDS_FRAGMENT,
  TARGET_FRAGMENT,
  TARGET_PARAMS_FRAGMENT,
  UI_CARD_FRAGMENT,
  UI_CARD_WIDE_FRAGMENT,
  UI_LINES_FRAGMENT,
  UI_PIN_FIELDS_FRAGMENT,
} from './fragments';

import {
  SUPPORTED_ACTIONS,
  SUPPORTED_BLOCKS,
  SUPPORTED_CONTROLS,
  SUPPORTED_FEATURES,
  SUPPORTED_LAYOUTS,
  SUPPORTED_TARGETS,
  SUPPORTED_THEMES,
} from './constants';

const FULFILLMENT_TIMES_QUERY_BODY = `
  fulfillmentTimes: fulfillment_times(
    fulfillment_methods: $fulfillment_methods
    location: $location
    uuid: $uuid
  ) {
    fulfillmentTimeMethods: fulfillment_time_methods {
      fulfillmentMethodLabel: fulfillment_method_label
      fulfillmentMethod: fulfillment_method
      asap {
        ...fulfillmentTimeOptionFields
      }
      days {
        day
        dayLabel: day_label
        times {
          ...fulfillmentTimeOptionFields
        }
      }
    }
  }
`;

export function getHomeFeed(variables) {
  return {
    query: `
      query getHomeFeed(
        $ui_actions: [UIActionType!]
        $ui_blocks: [UIBlockType!]
        $ui_controls: [UIControlType!]
        $ui_features: [UIFeatureType!]
        $ui_layouts: [UILayoutType!]
        $ui_targets: [UITargetType!]
        $ui_themes: [UIThemeType!]
        $fulfillment_methods: [FulfillmentMethod!]
        $location: LocationInput!
        $url: String
        $options: SearchOptionsInput
        $uuid: String!
      ) {
        results: search(
          location: $location
          options: $options
          url: $url
          capabilities: {
            ui_actions: $ui_actions,
            ui_blocks: $ui_blocks,
            ui_controls: $ui_controls,
            ui_features: $ui_features,
            ui_layouts: $ui_layouts,
            ui_targets: $ui_targets,
            ui_themes: $ui_themes,
            fulfillment_methods: $fulfillment_methods
          }
          uuid: $uuid
        ) {
          layoutGroups: ui_layout_groups {
            id
            subheader
            data: ui_layouts { ...uiLayoutFields }
          }

          controlGroups: ui_control_groups {
            appliedFilters: applied_filters { ...uiControlAppliedFilterFields }
            filters { ...uiControlFilterFields }
            sort { ...uiControlFilterFields }
            queryResults: query_results { ...uiControlQueryResultFields }
            fulfillmentMethods: fulfillment_methods { ...uiControlFulfillmentMethodFields }
          }

          meta {
            ...searchResultMetaFields
          }
        }

        ${FULFILLMENT_TIMES_QUERY_BODY}
      }
      ${FULFILLMENT_TIME_OPTION_FIELDS_FRAGMENT}
      ${FULFILLMENT_METHOD_FIELDS_FRAGMENT}
      ${SEARCH_FIELDS_FRAGMENT}
      ${TARGET_FRAGMENT}
      ${RESTAURANT_FRAGMENT}
      ${CURRENCY_FRAGMENT}
      ${BLOCK_FRAGMENT}
      ${LAYOUT_FIELDS_FRAGMENT}
      ${CONTROL_FIELDS_FRAGMENT}
      ${APPLIED_FILTER_FIELDS_FRAGMENT}
      ${TARGET_PARAMS_FRAGMENT}
      ${QUERY_RESULTS_FRAGMENT}
      ${COLOR_FIELDS_FRAGMENT}
      ${DELIVEROO_ICON_FRAGMENT}
      ${UI_CARD_FRAGMENT}
      ${UI_LINES_FRAGMENT}
    `,
    variables: {
      ui_actions: SUPPORTED_ACTIONS,
      ui_blocks: SUPPORTED_BLOCKS,
      ui_controls: SUPPORTED_CONTROLS,
      ui_features: SUPPORTED_FEATURES,
      ui_layouts: SUPPORTED_LAYOUTS,
      ui_targets: SUPPORTED_TARGETS,
      ui_themes: SUPPORTED_THEMES,
      ...variables,
    },
  };
}

export function getFulfillmentTimes({ fulfillmentMethods, location, uuid }) {
  return {
    query: `
      query getFulfillmentTimes(
        $fulfillment_methods: [FulfillmentMethod!]
        $location: LocationInput!
        $uuid: String!
      ) {
        ${FULFILLMENT_TIMES_QUERY_BODY}
      }
      ${FULFILLMENT_TIME_OPTION_FIELDS_FRAGMENT}
    `,
    variables: {
      fulfillment_methods: fulfillmentMethods,
      location,
      uuid,
    },
  };
}

export function getSearchResults(variables) {
  return {
    query: `
      query getSearchResults(
        $ui_actions: [UIActionType!]
        $ui_blocks: [UIBlockType!]
        $ui_controls: [UIControlType!]
        $ui_features: [UIFeatureType!]
        $ui_layouts: [UILayoutType!]
        $ui_targets: [UITargetType!]
        $ui_themes: [UIThemeType!]
        $fulfillment_methods: [FulfillmentMethod!]
        $location: LocationInput!
        $url: String
        $options: SearchOptionsInput
        $uuid: String!
      ) {
        results: search(
          location: $location
          options: $options
          url: $url
          capabilities: {
            ui_actions: $ui_actions,
            ui_blocks: $ui_blocks,
            ui_controls: $ui_controls,
            ui_features: $ui_features,
            ui_layouts: $ui_layouts,
            ui_targets: $ui_targets,
            ui_themes: $ui_themes,
            fulfillment_methods: $fulfillment_methods
          }
          uuid: $uuid
        ) {
          controlGroups: ui_control_groups {
            queryResults: query_results { ...uiControlQueryResultFields }
          }
          layoutGroups: ui_layout_groups {
            id
            subheader
            data: ui_layouts { ...uiLayoutFields }
          }
          meta {
            options {
              query
            }
          }
        }
      }
      ${TARGET_FRAGMENT}
      ${TARGET_PARAMS_FRAGMENT}
      ${QUERY_RESULTS_FRAGMENT}
      ${DELIVEROO_ICON_FRAGMENT}
      ${SEARCH_BLOCK_FRAGMENT}
      ${SEARCH_LAYOUT_FIELDS_FRAGMENT}
      ${UI_LINES_FRAGMENT}
      ${COLOR_FIELDS_FRAGMENT}
    `,
    variables: {
      ui_actions: SUPPORTED_ACTIONS,
      ui_blocks: SUPPORTED_BLOCKS,
      ui_controls: SUPPORTED_CONTROLS,
      ui_features: SUPPORTED_FEATURES,
      ui_layouts: SUPPORTED_LAYOUTS,
      ui_targets: SUPPORTED_TARGETS,
      ui_themes: SUPPORTED_THEMES,
      ...variables,
    },
  };
}

export const getHomeMapView = ({ location, options, uuid }) => ({
  query: `
    query getHomeMapView(
      $location: LocationInput!
      $options: SearchOptionsInput
      $ui_actions: [UIActionType!]
      $ui_blocks: [UIBlockType!]
      $ui_controls: [UIControlType!]
      $ui_features: [UIFeatureType!]
      $ui_layouts: [UILayoutType!]
      $ui_targets: [UITargetType!]
      $ui_themes: [UIThemeType!]
      $uuid: String!
    ) {
      results: home_map_view(
        capabilities: {
          ui_actions: $ui_actions,
          ui_blocks: $ui_blocks,
          ui_controls: $ui_controls,
          ui_features: $ui_features,
          ui_layouts: $ui_layouts,
          ui_targets: $ui_targets,
          ui_themes: $ui_themes,
        }
        location: $location
        options: $options
        uuid: $uuid
      ) {
        layoutGroups: ui_layout_groups {
          id
          subheader
          data: ui_layouts { ...uiLayoutFields }
        }
        uiMap: ui_map {
          pins { ...uiPinFields }
        }
      }
    }
    ${COLOR_FIELDS_FRAGMENT}
    ${DELIVEROO_ICON_FRAGMENT}
    ${MAP_BLOCK_FRAGMENT}
    ${MAP_LAYOUT_FIELDS_FRAGMENT}
    ${TARGET_FRAGMENT}
    ${TARGET_PARAMS_FRAGMENT}
    ${UI_CARD_WIDE_FRAGMENT}
    ${UI_LINES_FRAGMENT}
    ${UI_PIN_FIELDS_FRAGMENT}
  `,
  variables: {
    location,
    options,
    ui_actions: SUPPORTED_ACTIONS,
    ui_blocks: SUPPORTED_BLOCKS,
    ui_controls: SUPPORTED_CONTROLS,
    ui_features: SUPPORTED_FEATURES,
    ui_layouts: SUPPORTED_LAYOUTS,
    ui_targets: SUPPORTED_TARGETS,
    ui_themes: SUPPORTED_THEMES,
    uuid,
  },
});
